import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';

import Icon from 'components/icon/Icon';

interface ScrubberHandleProps {
  containerRef: React.RefObject<HTMLElement | null>;
  activeIndex: number;
  ariaLabel?: string;
  labels?: string[];
  onSelect?: (index: number) => void;
  onScrub?: (index: number) => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const ScrubberHandle: React.FC<ScrubberHandleProps> = ({
  containerRef,
  activeIndex,
  ariaLabel,
  labels,
  onSelect,
  onScrub
}) => {
  const [offset, setOffset] = useState(0); // px from the rail-area's left edge to the pill's left edge
  const [index, setIndex] = useState(0);
  const [count, setCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const indexRef = useRef(0);
  const handleRef = useRef<HTMLButtonElement>(null);
  const dragging = useRef(false);
  const dotCentersRef = useRef<number[]>([]);
  const scrubRaf = useRef(0);

  const getDots = useCallback(() => {
    const el = containerRef.current;
    if (!el) return [] as HTMLElement[];
    return Array.from(el.querySelectorAll<HTMLElement>('.kvinnehistorie-scrubber--dot')).filter(
      dot => dot.getBoundingClientRect().width > 0
    );
  }, [containerRef]);

  const positionAt = useCallback((centerPx: number, width: number) => {
    const half = handleRef.current ? handleRef.current.offsetWidth / 2 : 0;
    const center = clamp(centerPx, half, Math.max(half, width - half));
    setOffset(center - half);
  }, []);

  const snapToIndex = useCallback(
    (next: number) => {
      const el = containerRef.current;
      if (!el) return;

      const dots = getDots();
      if (dots.length === 0) return;

      const i = clamp(next, 0, dots.length - 1);
      const containerRect = el.getBoundingClientRect();
      const dotRect = dots[i].getBoundingClientRect();
      positionAt(dotRect.left + dotRect.width / 2 - containerRect.left, containerRect.width);
      setCount(dots.length);
      setIndex(i);
      indexRef.current = i;
    },
    [containerRef, getDots, positionAt]
  );

  const nearestIndex = (clientX: number) => {
    const centers = dotCentersRef.current;
    let nearest = 0;
    let best = Infinity;
    centers.forEach((center, i) => {
      const distance = Math.abs(center - clientX);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });
    return nearest;
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    dragging.current = true;
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    dotCentersRef.current = getDots().map(dot => {
      const rect = dot.getBoundingClientRect();
      return rect.left + rect.width / 2;
    });
    e.preventDefault();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging.current) return;
    const el = containerRef.current;
    if (!el || dotCentersRef.current.length === 0) return;
    const containerRect = el.getBoundingClientRect();
    positionAt(e.clientX - containerRect.left, containerRect.width);
    // On each decade crossed, scrub there live (coalesced to one scroll per frame).
    const nearest = nearestIndex(e.clientX);
    if (nearest !== indexRef.current) {
      setIndex(nearest);
      indexRef.current = nearest;
      if (onScrub) {
        if (scrubRaf.current) cancelAnimationFrame(scrubRaf.current);
        scrubRaf.current = requestAnimationFrame(() => {
          scrubRaf.current = 0;
          onScrub(nearest);
        });
      }
    }
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    setIsDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (scrubRaf.current) {
      cancelAnimationFrame(scrubRaf.current);
      scrubRaf.current = 0;
    }
    snapToIndex(indexRef.current);
    if (onSelect) onSelect(indexRef.current);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    let next: number;
    if (e.key === 'ArrowLeft') {
      next = indexRef.current - 1;
    } else if (e.key === 'ArrowRight') {
      next = indexRef.current + 1;
    } else {
      return;
    }
    e.preventDefault();
    const total = count || getDots().length;
    if (next < 0 || next >= total) return;
    snapToIndex(next);
    if (onSelect) onSelect(next);
  };

  // Follow the page's active milestone, unless the user is mid-drag. The dots may not have a size yet
  // on the first pass (the rail has just mounted), so try again on the next frame.
  useLayoutEffect(() => {
    if (dragging.current) return;
    if (getDots().length === 0) {
      const frame = requestAnimationFrame(() => snapToIndex(Math.max(0, activeIndex)));
      return () => cancelAnimationFrame(frame);
    }
    snapToIndex(Math.max(0, activeIndex));
  }, [activeIndex, snapToIndex, getDots]);

  // Keep the handle pinned to its dot when the rail moves under it (resize, track scroll).
  useEffect(() => {
    const el = containerRef.current;
    const realign = () => {
      if (!dragging.current) {
        snapToIndex(indexRef.current);
      }
    };

    window.addEventListener('resize', realign);

    const tracks = el ? Array.from(el.querySelectorAll('.kvinnehistorie-scrubber--track')) : [];
    tracks.forEach(track => track.addEventListener('scroll', realign, { passive: true }));

    return () => {
      window.removeEventListener('resize', realign);
      tracks.forEach(track => track.removeEventListener('scroll', realign));
    };
  }, [containerRef, snapToIndex]);

  useEffect(
    () => () => {
      if (scrubRaf.current) cancelAnimationFrame(scrubRaf.current);
    },
    []
  );

  return (
    <button
      ref={handleRef}
      type="button"
      className={cn('kvinnehistorie-scrubber--handle', { '-dragging': isDragging })}
      style={{ transform: `translate3d(${offset}px, 0, 0)` }}
      role="slider"
      aria-label={ariaLabel || 'Skrubb tidslinjen'}
      aria-valuemin={0}
      aria-valuemax={Math.max(0, count - 1)}
      aria-valuenow={index}
      aria-valuetext={labels?.[index]}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    >
      <span className="kvinnehistorie-scrubber--handle-arrow" aria-hidden="true">
        <Icon name="arrow-left" />
      </span>
      <span className="kvinnehistorie-scrubber--handle-dot" aria-hidden="true" />
      <span className="kvinnehistorie-scrubber--handle-arrow" aria-hidden="true">
        <Icon name="arrow-right" />
      </span>
    </button>
  );
};

ScrubberHandle.displayName = 'ScrubberHandle';

export default ScrubberHandle;
